import React, { useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TablePagination, Chip, Box, Typography, CircularProgress,
    Collapse, IconButton
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const ORG_COLORS = {
    primary: '#2D4A3D',
    text: '#1a1a2e',
    textSecondary: '#8a8f9d',
    border: '#eaecf0',
    backgroundLight: '#F8F9FB',
};

const statusChip = (status) => {
    const map = {
        Completed: { label: 'Completed', bg: '#e8f5e9', color: '#2e7d32' },
        Pending:   { label: 'Pending',   bg: '#fff3e0', color: '#e65100' },
        Failed:    { label: 'Failed',     bg: '#ffebee', color: '#c62828' },
    };
    const s = map[status] || { label: status, bg: '#f5f5f5', color: ORG_COLORS.textSecondary };
    return (
        <Chip
            label={s.label}
            size="small"
            sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.72rem' }}
        />
    );
};

const ExpandableRow = ({ application }) => {
    const [open, setOpen] = useState(false);

    let fieldData = {};
    try {
        fieldData = typeof application.field_data === 'string'
            ? JSON.parse(application.field_data)
            : (application.field_data || {});
    } catch (_) {}

    const hasExtra = Object.keys(fieldData).length > 0;

    return (
        <>
            <TableRow sx={{ '&:hover': { bgcolor: ORG_COLORS.backgroundLight } }}>
                <TableCell sx={{ width: 36, pr: 0 }}>
                    {hasExtra && (
                        <IconButton size="small" onClick={() => setOpen(!open)}>
                            <FontAwesomeIcon
                                icon={open ? faChevronUp : faChevronDown}
                                style={{ fontSize: '11px', color: ORG_COLORS.textSecondary }}
                            />
                        </IconButton>
                    )}
                </TableCell>
                <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600, color: ORG_COLORS.text }}>
                    {application.applicant_name}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: ORG_COLORS.textSecondary }}>
                    {application.applicant_email}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: ORG_COLORS.text }}>
                    {application.category_name}
                    <Typography component="span" sx={{ fontSize: '0.7rem', color: ORG_COLORS.textSecondary, ml: 0.5 }}>
                        ({application.billing_cycle})
                    </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: ORG_COLORS.text }}>
                    ${parseFloat(application.base_amount || 0).toFixed(2)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: ORG_COLORS.textSecondary }}>
                    {parseFloat(application.voluntary_amount || 0) > 0
                        ? `+$${parseFloat(application.voluntary_amount).toFixed(2)}`
                        : '—'}
                </TableCell>
                <TableCell>{statusChip(application.payment_status)}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: ORG_COLORS.textSecondary }}>
                    {application.payment_method || '—'}
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', color: ORG_COLORS.textSecondary }}>
                    {new Date(application.created_at).toLocaleDateString()}
                </TableCell>
            </TableRow>
            {hasExtra && (
                <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 4, py: 1.5, bgcolor: '#fafbfc', borderBottom: `1px solid ${ORG_COLORS.border}` }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: ORG_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Additional Information
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                                    {Object.entries(fieldData).map(([key, value]) => (
                                        <Box key={key}>
                                            <Typography variant="caption" sx={{ color: ORG_COLORS.textSecondary, fontSize: '0.72rem', display: 'block' }}>
                                                {key.replace(/_\d+$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.82rem', color: ORG_COLORS.text, fontWeight: 500 }}>
                                                {Array.isArray(value) ? value.join(', ') : String(value || '—')}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

const ApplicationsTable = ({ applications, total, page, rowsPerPage, loading, onPageChange, onRowsPerPageChange }) => {
    return (
        <Box>
            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: `1px solid ${ORG_COLORS.border}`, boxShadow: 'none' }}>
                <Table sx={{ minWidth: 700 }}>
                    <TableHead sx={{ bgcolor: ORG_COLORS.backgroundLight }}>
                        <TableRow>
                            <TableCell sx={{ width: 36 }} />
                            {['Name', 'Email', 'Category', 'Amount', 'Voluntary', 'Status', 'Payment', 'Date'].map(h => (
                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.8rem', color: ORG_COLORS.textSecondary }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={28} sx={{ color: ORG_COLORS.primary }} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && applications.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4, color: ORG_COLORS.textSecondary, fontSize: '0.875rem' }}>
                                    No applications found.
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && applications.map((app) => (
                            <ExpandableRow key={app.id} application={app} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{ mt: 0.5 }}
            />
        </Box>
    );
};

export default ApplicationsTable;
